import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Crown, LockKey, PlusCircle, Trash, ArrowLeft, Copy, Check, ShieldCheck } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";
import { BrutalButton, BrutalCard, BrutalInput } from "../components/Brutal";
import { useAuth } from "../AuthContext";

/**
 * v400.37 — Super-Owner allowlist admin (Moin-only).
 *
 * Two-tier model:
 *   1. Env-locked (LockKey) — the `SUPER_OWNER_EMAILS` env var. Read-only in
 *      this UI; edit `/app/backend/.env` + restart to change. Fail-safe layer.
 *   2. DB-managed (Trash removable) — additions from this UI, persisted to
 *      `app_settings.super_owner_extras`. Zero restart needed.
 *
 * `is_super_owner()` on the backend uses the union. `/api/config` returns the
 * union; `AuthContext` hydrates `isSuperOwner` from it on every auth event.
 */
export default function SuperOwnersPage() {
  const { t } = useTranslation();
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null); // email currently being removed
  const [copiedField, setCopiedField] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/settings/super-owners");
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const normalized = email.trim().toLowerCase();
    if (!normalized || !normalized.includes("@")) {
      toast.error(t("super_owners.invalid_email"));
      return;
    }
    setAdding(true);
    try {
      await api.post("/settings/super-owners", { email: normalized });
      toast.success(t("super_owners.added", { email: normalized }));
      setEmail("");
      await load();
      // Refresh AuthContext so the newly-added user's isSuperOwner flag
      // propagates to any currently-open sessions on this device.
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (target) => {
    if (!window.confirm(t("super_owners.remove_confirm", { email: target }))) return;
    setRemoving(target);
    try {
      await api.delete(`/settings/super-owners/${encodeURIComponent(target)}`);
      toast.success(t("super_owners.removed", { email: target }));
      await load();
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRemoving(null);
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1800);
    } catch {
      toast.error(t("super_owners.copy_failed"));
    }
  };

  const envString = data
    ? `SUPER_OWNER_EMAILS=${data.effective.join(",")}`
    : "";

  return (
    <div className="max-w-3xl space-y-6" data-testid="super-owners-page">
      <div>
        <Link
          to="/settings/password"
          className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900 mb-2"
          data-testid="back-to-settings"
        >
          <ArrowLeft size={14} weight="bold" /> {t("common.back")}
        </Link>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2">
          <Crown size={26} weight="fill" className="text-[#c9a24a]" />
          {t("super_owners.title")}
        </h1>
        <p className="text-sm sm:text-base text-zinc-600 mt-1">
          {t("super_owners.subtitle")}
        </p>
      </div>

      {/* Env-locked (immutable) */}
      <BrutalCard>
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-zinc-100 border border-zinc-200 flex items-center justify-center shrink-0">
            <LockKey size={18} weight="bold" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold">{t("super_owners.env_title")}</h2>
            <p className="text-xs text-zinc-500 mt-0.5">
              {t("super_owners.env_hint")}
            </p>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-zinc-500">{t("common.loading")}</p>
        ) : (
          <ul className="divide-y divide-zinc-100" data-testid="env-emails-list">
            {data.env_emails.map((e) => (
              <li
                key={e}
                className="flex items-center justify-between py-2.5"
                data-testid={`env-email-${e}`}
              >
                <span className="text-sm font-medium truncate">{e}</span>
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-zinc-100 border border-zinc-200 text-zinc-600 uppercase tracking-wide">
                  {t("super_owners.locked_badge")}
                </span>
              </li>
            ))}
          </ul>
        )}
      </BrutalCard>

      {/* DB-managed (mutable) */}
      <BrutalCard>
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-[#fbf6e7] border-2 border-[#c9a24a] flex items-center justify-center shrink-0">
            <PlusCircle size={18} weight="bold" className="text-[#8a6a1c]" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold">{t("super_owners.db_title")}</h2>
            <p className="text-xs text-zinc-500 mt-0.5">
              {t("super_owners.db_hint")}
            </p>
          </div>
        </div>

        <form onSubmit={handleAdd} className="flex flex-col sm:flex-row gap-2 mb-4">
          <BrutalInput
            type="email"
            value={email}
            onChange={(ev) => setEmail(ev.target.value)}
            placeholder={t("super_owners.email_placeholder")}
            autoComplete="off"
            spellCheck={false}
            className="flex-1"
            data-testid="add-super-owner-input"
            required
          />
          <BrutalButton
            type="submit"
            variant="eliteFilled"
            disabled={adding || !email.trim()}
            data-testid="add-super-owner-submit"
          >
            {adding ? t("common.saving") : t("super_owners.add_button")}
          </BrutalButton>
        </form>

        {loading ? (
          <p className="text-sm text-zinc-500">{t("common.loading")}</p>
        ) : data.extra_emails.length === 0 ? (
          <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-4 text-sm text-zinc-500 text-center" data-testid="db-emails-empty">
            {t("super_owners.db_empty")}
          </div>
        ) : (
          <ul className="divide-y divide-zinc-100" data-testid="db-emails-list">
            {data.extra_emails.map((e) => (
              <li
                key={e}
                className="flex items-center justify-between py-2.5 gap-3"
                data-testid={`db-email-${e}`}
              >
                <span className="text-sm font-medium truncate">{e}</span>
                <BrutalButton
                  size="sm"
                  variant="warning"
                  onClick={() => handleRemove(e)}
                  disabled={removing === e}
                  data-testid={`remove-super-owner-${e}`}
                >
                  <Trash size={14} weight="bold" />
                  {removing === e ? t("common.saving") : t("super_owners.remove_button")}
                </BrutalButton>
              </li>
            ))}
          </ul>
        )}

        {data?.updated_at && (
          <p className="text-[11px] text-zinc-400 mt-4">
            {t("super_owners.audit_footer", {
              email: data.updated_by_email || "system",
              time: new Date(data.updated_at).toLocaleString(),
            })}
          </p>
        )}
      </BrutalCard>

      {/* Info: alternative env-var path */}
      <BrutalCard>
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-zinc-100 border border-zinc-200 flex items-center justify-center shrink-0">
            <ShieldCheck size={18} weight="bold" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold">{t("super_owners.env_alt_title")}</h2>
            <p className="text-xs text-zinc-500 mt-0.5">
              {t("super_owners.env_alt_hint")}
            </p>
          </div>
        </div>

        {!loading && (
          <>
            <label className="block text-xs font-semibold mb-1.5 text-zinc-600">
              {t("super_owners.env_line_label")}
            </label>
            <div className="relative">
              <pre
                className="bg-zinc-950 text-zinc-100 border border-zinc-950 rounded-lg p-3 pr-14 text-xs font-mono overflow-x-auto"
                data-testid="env-line-preview"
              >
                <code>{envString}</code>
              </pre>
              <button
                type="button"
                onClick={() => copyToClipboard(envString, "env-line")}
                data-testid="copy-env-line"
                className="absolute top-2 right-2 h-8 w-8 rounded-md bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                title={t("super_owners.copy")}
              >
                {copiedField === "env-line" ? (
                  <Check size={14} weight="bold" />
                ) : (
                  <Copy size={14} weight="bold" />
                )}
              </button>
            </div>

            <label className="block text-xs font-semibold mb-1.5 mt-4 text-zinc-600">
              {t("super_owners.restart_cmd_label")}
            </label>
            <div className="relative">
              <pre
                className="bg-zinc-950 text-zinc-100 border border-zinc-950 rounded-lg p-3 pr-14 text-xs font-mono overflow-x-auto"
                data-testid="restart-cmd"
              >
                <code>sudo supervisorctl restart backend</code>
              </pre>
              <button
                type="button"
                onClick={() => copyToClipboard("sudo supervisorctl restart backend", "restart-cmd")}
                data-testid="copy-restart-cmd"
                className="absolute top-2 right-2 h-8 w-8 rounded-md bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                title={t("super_owners.copy")}
              >
                {copiedField === "restart-cmd" ? (
                  <Check size={14} weight="bold" />
                ) : (
                  <Copy size={14} weight="bold" />
                )}
              </button>
            </div>

            <p className="text-[11px] text-zinc-500 mt-3 leading-relaxed">
              {t("super_owners.env_alt_note")}
            </p>
          </>
        )}
      </BrutalCard>
    </div>
  );
}
